import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { Specification } from 'gql/autogen/Specification';
import { filterPossibleOfferOptions } from 'component/page/detail/store/availabilityIndex';
import { MappedOfferOptionKey } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferOptions/OfferOptions.types';
import { cheapestOfferOptionSort } from 'gql/service/graph/activityService';

export const TIME_KEY = 'time';
export const getAllSpecifications = (offerOptions?: EnrichedOfferOption[]) => {
    if (!offerOptions) return [];
    return offerOptions.reduce<Specification[]>((acc, option) => {
        const { specification: spec } = option;
        if (spec) {
            return [...acc, ...spec];
        }
        return acc;
    }, []);
};

export const mapOfferOptions = (offerOptions?: EnrichedOfferOption[], isSortOfferOption?: boolean): MappedOfferOptionKey[] => {
    const specifications = getAllSpecifications(offerOptions);
    const keys = new Set(specifications.map((spec) => spec.key));
    const result = Array.from(keys).map((key) => {
        const mappedSpecs = specifications
            .filter((spec) => spec.key === key)
            .map((spec) => spec.specification)
            .sort((a, b) => {
                if (!isSortOfferOption) {
                    return (a || '').localeCompare(b || '');
                }
                return cheapestOfferOptionSort(a, b);
            });
        const values = Array.from(new Set(mappedSpecs));
        return { key, values };
    });

    return result;
};

export const getOrderedKeysSpecifications = (offerOptions?: EnrichedOfferOption[]): (string | undefined)[] => {
    const specifications = getAllSpecifications(offerOptions);
    const keys = new Set(specifications.map((spec) => spec.key));
    return Array.from(keys);
};

export const getSpecification = (key: string | undefined, specification: string | undefined): Specification => {
    return { key, specification };
};

export const getTimeSlotSpecification = (timeSlot: string | undefined): Specification => {
    return getSpecification(TIME_KEY, timeSlot);
};

export const getNonExistingKeys = (generalSpecification: Specification[], selectedSpecifications: Specification[]) => {
    const nonExistingKeysInSelected = generalSpecification.filter((general) => !selectedSpecifications.some((selected) => selected.key === general.key)).map((sp) => sp.key);
    return nonExistingKeysInSelected;
};

export const isSameSpecification = (spec1: Specification, spec2: Specification) => {
    return spec1.key === spec2.key && spec1.specification === spec2.specification;
};

export const isSameSpecifications = (spec1: Specification[], spec2: Specification[]) => {
    if (spec1.length !== spec2.length) return false;
    return spec1.every((s1) => spec2.some((s2) => isSameSpecification(s1, s2)));
};

export const isSpecificationExist = (specification: Specification, specifications: Specification[]) => {
    return specifications.some((spec) => isSameSpecification(spec, specification));
};

export const isOfferOptionAvailable = (allOfferOptions: EnrichedOfferOption[], selectedSpecifications: Specification[], checkingSpecification: Specification) => {
    if (allOfferOptions.length === 0) return false;
    const otherSelectedSpecifications = selectedSpecifications.filter((spec) => spec.key !== checkingSpecification.key);
    const possibleOfferOptions = filterPossibleOfferOptions(allOfferOptions, otherSelectedSpecifications);
    return possibleOfferOptions.some((offerOption) => offerOption.isAvailable && isSpecificationExist(checkingSpecification, offerOption.specification || []));
};
